import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';
import { eq } from 'ember-truth-helpers';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import tw from '../../helpers/tw';
import type ItemsIndexRoute from '../../routes/explore/index.ts';
import { t } from 'ember-intl';
import MobileHeader from '../../components/mobile-header.gts';
import Icon from '../../components/icon';
import type NetworkStatus from '../../services/network-status';
import AlertTriangleIcon from '~icons/lucide/alert-triangle';
import RefreshCwIcon from '~icons/lucide/refresh-cw';

interface Signature {
  Args: {
    model: Awaited<ReturnType<ItemsIndexRoute['model']>>;
  };
  Blocks: {
    default: [];
  };
}

export default class ItemsIndexTemplate extends Component<Signature> {
  @service declare router: RouterService;
  @service declare intl: IntlService;
  @service declare networkStatus: NetworkStatus;

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    const unsubscribe = this.networkStatus.onReconnect(() => {
      if (this.args.model.loadError) {
        void this.router.refresh();
      }
    });
    registerDestructor(this, unsubscribe);
  }

  @action async refresh(): Promise<void> {
    await this.router.refresh();
  }

  get types() {
    return [
      { key: '', label: this.intl.t('common.all') },
      { key: 'movie', label: this.intl.t('types.plural.movie') },
      { key: 'tv_show', label: this.intl.t('types.plural.tv_show') },
      { key: 'book', label: this.intl.t('types.plural.book') },
      { key: 'music', label: this.intl.t('types.plural.music') },
      { key: 'podcast', label: this.intl.t('types.plural.podcast') },
      { key: 'recipe', label: this.intl.t('types.plural.recipe') },
      { key: 'video_game', label: this.intl.t('types.plural.video_game') },
      { key: 'place', label: this.intl.t('types.plural.place') },
      { key: 'product', label: this.intl.t('types.plural.product') },
    ];
  }

  get sorts() {
    return [
      { key: '-createdAt', label: this.intl.t('sort.recently_added') },
      { key: '-matchedCount', label: this.intl.t('sort.most_mentioned') },
      { key: 'name', label: this.intl.t('sort.a_to_z') },
      {
        key: '-recommendationPct',
        label: this.intl.t('sort.highest_consensus'),
      },
      { key: '-trendingDay', label: this.intl.t('sort.trending_today') },
      { key: '-trendingWeek', label: this.intl.t('sort.trending_week') },
      { key: '-trendingMonth', label: this.intl.t('sort.trending_month') },
      { key: '-trendingYear', label: this.intl.t('sort.trending_year') },
    ];
  }

  get recipeSorts() {
    return [
      { key: '-createdAt', label: this.intl.t('sort.recently_added') },
      { key: '-updatedAt', label: this.intl.t('sort.recently_updated') },
      { key: 'name', label: this.intl.t('sort.a_to_z') },
    ];
  }

  get activeType(): string {
    return (
      (this.router.currentRoute?.queryParams?.['type'] as string | undefined) ??
      ''
    );
  }

  get activeSort(): string {
    return (
      (this.router.currentRoute?.queryParams?.['sort'] as string | undefined) ??
      '-createdAt'
    );
  }

  get activeQuery(): string {
    return (
      (this.router.currentRoute?.queryParams?.['q'] as string | undefined) ?? ''
    );
  }

  changeSort = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    this.router.transitionTo('explore.index.results', {
      queryParams: {
        sort: target.value,
        type: this.activeType || undefined,
        q: this.activeQuery || undefined,
        page: 1,
      },
    });
  };

  <template>
    <MobileHeader @title={{t "common.items"}} />

    <h1 class="mb-6 hidden text-3xl font-bold lg:block">{{t
        "common.items"
      }}</h1>

    {{#if @model.loadError}}
      <div class="alert alert-warning mb-4 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <Icon @icon={{AlertTriangleIcon}} class="h-5 w-5 shrink-0" />
          <span>Couldn't load category counts.</span>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-ghost gap-1"
          {{on "click" this.refresh}}
        >
          <Icon @icon={{RefreshCwIcon}} class="h-4 w-4" />
          Retry
        </button>
      </div>
    {{/if}}

    <div class="tabs tabs-box scrollbar-hide mb-4 flex-nowrap overflow-x-auto">
      {{#each this.types as |t|}}
        <LinkTo
          @route="explore.index.results"
          @query={{hash
            type=t.key
            sort=this.activeSort
            q=this.activeQuery
            page="1"
          }}
          class={{tw "tab" (if (eq this.activeType t.key) "tab-active")}}
        >
          {{t.label}}
          {{#if (tabBadge @model.itemCountsByType @model.totalRecipes t.key)}}
            <span class="badge badge-xs ml-1 opacity-70">
              {{tabBadge @model.itemCountsByType @model.totalRecipes t.key}}
            </span>
          {{/if}}
        </LinkTo>
      {{/each}}
    </div>

    <div class="mb-6 flex flex-wrap items-center gap-2">
      <label class="select">
        <span class="label">{{t "common.sort_by"}}</span>
        <select value={{this.activeSort}} {{on "change" this.changeSort}}>
          {{#each
            (if (eq this.activeType "recipe") this.recipeSorts this.sorts)
            as |s|
          }}
            <option value={{s.key}}>{{s.label}}</option>
          {{/each}}
        </select>
      </label>
    </div>

    {{outlet}}
  </template>
}

function tabBadge(
  counts: Record<string, number> | undefined,
  totalRecipes: number,
  key: string
): number | undefined {
  if (!key) return undefined;
  if (key === 'recipe') return totalRecipes || undefined;
  return counts?.[key];
}
